import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  useAllOvertimeRecords,
  useManagerEmployees,
} from '@saiens/shared/hooks';
import { formatWorkHours, getOvertimeStateLabel } from '@saiens/shared/utils';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { ArrowLeft, ChevronDown, ChevronUp } from 'lucide-react';

const STATE_OPTIONS = [
  { value: '', label: '全部' },
  { value: 'draft', label: '草稿' },
  { value: 'f_approve', label: '待核准' },
  { value: 'agree', label: '已核准' },
  { value: 'refused', label: '已拒絕' },
];

const STATE_COLORS: Record<string, string> = {
  draft: 'bg-gray-100 text-gray-600',
  f_approve: 'bg-yellow-100 text-yellow-700',
  agree: 'bg-green-100 text-green-700',
  refused: 'bg-red-100 text-red-700',
};

const PAGE_SIZE = 20;

export function AllOvertimeRecordsPage() {
  const navigate = useNavigate();
  const [showFilters, setShowFilters] = useState(false);
  const [state, setState] = useState('');
  const [employeeId, setEmployeeId] = useState<number | undefined>();
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [offset, setOffset] = useState(0);

  const { data: employees } = useManagerEmployees();
  const { data, isLoading } = useAllOvertimeRecords({
    offset,
    limit: PAGE_SIZE,
    state: state || undefined,
    employee_id: employeeId,
    date_from: dateFrom || undefined,
    date_to: dateTo || undefined,
  });

  const handleReset = () => {
    setState('');
    setEmployeeId(undefined);
    setDateFrom('');
    setDateTo('');
    setOffset(0);
  };

  return (
    <div className="mx-auto max-w-lg">
      {/* Header */}
      <div className="sticky top-0 z-10 flex items-center justify-between bg-white px-4 py-3 shadow-sm">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate(-1)} className="rounded-full p-1 hover:bg-gray-100">
            <ArrowLeft className="h-5 w-5" />
          </button>
          <h1 className="text-lg font-semibold">所有加班紀錄</h1>
        </div>
        <button
          onClick={() => setShowFilters((v) => !v)}
          className="flex items-center gap-1 text-sm text-indigo-600"
        >
          篩選
          {showFilters ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
        </button>
      </div>

      {/* Filters */}
      {showFilters && (
        <div className="space-y-3 border-b bg-gray-50 px-4 py-3">
          <div>
            <label className="mb-1 block text-xs text-gray-500">狀態</label>
            <select
              value={state}
              onChange={(e) => { setState(e.target.value); setOffset(0); }}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
            >
              {STATE_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="mb-1 block text-xs text-gray-500">員工</label>
            <select
              value={employeeId || ''}
              onChange={(e) => { setEmployeeId(e.target.value ? Number(e.target.value) : undefined); setOffset(0); }}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
            >
              <option value="">全部員工</option>
              {employees?.map((emp) => (
                <option key={emp.id} value={emp.id}>{emp.name}</option>
              ))}
            </select>
          </div>
          <div className="flex gap-2">
            <div className="flex-1">
              <label className="mb-1 block text-xs text-gray-500">起始日</label>
              <input
                type="date"
                value={dateFrom}
                onChange={(e) => { setDateFrom(e.target.value); setOffset(0); }}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
              />
            </div>
            <div className="flex-1">
              <label className="mb-1 block text-xs text-gray-500">結束日</label>
              <input
                type="date"
                value={dateTo}
                onChange={(e) => { setDateTo(e.target.value); setOffset(0); }}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
              />
            </div>
          </div>
          <button
            onClick={handleReset}
            className="w-full rounded-lg border border-gray-300 py-2 text-sm text-gray-600"
          >
            重置篩選
          </button>
        </div>
      )}

      {/* List */}
      <div className="px-4 py-4">
        {isLoading ? (
          <div className="flex h-64 items-center justify-center">
            <LoadingSpinner size="lg" />
          </div>
        ) : !data || data.items.length === 0 ? (
          <div className="flex h-40 items-center justify-center text-sm text-gray-400">
            無加班紀錄
          </div>
        ) : (
          <>
            <div className="space-y-3">
              {data.items.map((r) => (
                <div
                  key={r.id}
                  className="rounded-xl bg-white p-4 shadow-sm"
                >
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-gray-900">{r.employee_name}</span>
                    <span
                      className={`rounded-full px-2 py-0.5 text-xs font-medium ${
                        STATE_COLORS[r.state] || 'bg-gray-100 text-gray-600'
                      }`}
                    >
                      {r.state_label || getOvertimeStateLabel(r.state)}
                    </span>
                  </div>
                  <p className="mt-1 text-xs text-gray-500">
                    {r.department_name} · {r.overtime_type_name}
                  </p>
                  <div className="mt-2 flex items-center justify-between text-xs text-gray-400">
                    <span>{r.start_day || '—'}</span>
                    <span className="font-semibold text-amber-600">
                      {formatWorkHours(r.duration_time)}
                    </span>
                  </div>
                </div>
              ))}
            </div>

            {/* Pagination */}
            {data.total > PAGE_SIZE && (
              <div className="mt-4 flex items-center justify-between">
                <button
                  onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
                  disabled={offset === 0}
                  className="rounded-lg border border-gray-300 px-3 py-1.5 text-sm disabled:opacity-40"
                >
                  上一頁
                </button>
                <span className="text-xs text-gray-400">
                  {offset + 1}-{Math.min(offset + PAGE_SIZE, data.total)} / {data.total}
                </span>
                <button
                  onClick={() => setOffset(offset + PAGE_SIZE)}
                  disabled={offset + PAGE_SIZE >= data.total}
                  className="rounded-lg border border-gray-300 px-3 py-1.5 text-sm disabled:opacity-40"
                >
                  下一頁
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
